import React, { memo, useCallback, useEffect, useState } from "react";
import moment from "moment";
import {
	Button,
	DatePicker,
	Select,
	Modal,
	Spin,
	Form,
	Checkbox,
	Tooltip,
} from "antd";
import classNames from "classnames";

import getReport from "../../store/thunks/getReport";
import getRegions from "../../requests/getRegions.js";
import { useSelector } from "react-redux";

import styles from "./DownloadReport.module.scss";
import TagsField from "./TagsField/TagsField";

const { Option } = Select;
const { MonthPicker } = DatePicker;

const DownloadReport = memo(({ closeModal }) => {
	const [allRegions, setAllRegions] = useState(false);
	const [regions, setRegions] = useState([]);
	const [selectedRegions, setSelectedRegions] = useState([]);
	const [selectedMeterType, setSelectedMeterType] = useState(null);
	const [loading, setLoading] = useState(false);
	const [date, setDate] = useState(
		+moment().format("DD") > 20 ? moment() : moment().subtract(1, "months")
	);
	const [selectedTag, setSelectedTag] = useState(false);
	const meterTypes = useSelector((store) => store.meterStore.meterTypes);
	const tags = useSelector((store) => store.meterStore.tags);

	useEffect(() => {
		(async () => {
			if (selectedMeterType) {
				setLoading(true);
				setRegions(await getRegions(selectedMeterType));
				setLoading(false);
			}
		})();
	}, [selectedMeterType]);

	useEffect(() => {
		if (loading) {
			setSelectedRegions([]);
			setAllRegions(false);
		}
	}, [selectedMeterType, loading]);

	const getMeterReport = useCallback(async () => {
		if (date && (selectedMeterType || selectedTag)) {
			setLoading(true);
			const [downloadURL, fileName] =
				(await getReport(date, selectedRegions, selectedMeterType, [
					selectedTag,
				])) || [];
			if (downloadURL) {
				const link = document.createElement("a");
				link.setAttribute("download", fileName);
				document.body.appendChild(link);
				link.href = downloadURL;
				link.click();
				closeModal();
			}
			setLoading(false);
		}
	}, [
		selectedRegions,
		date,
		selectedMeterType,
		meterTypes,
		regions,
		selectedTag,
	]);

	const selectAllRegions = useCallback((e) => {
		setSelectedRegions(undefined);
		setAllRegions(e.target.checked);
	}, []);

	const selectRegion = useCallback((value) => {
		setSelectedRegions(value);
	}, []);

	const selectMonth = useCallback((date) => {
		setDate(date);
	}, []);

	const disableDate = useCallback((date) => {
		if (date.isAfter(moment())) {
			return true;
		}
	}, []);

	const selectTag = useCallback((value) => {
		setSelectedTag(value);
		setSelectedMeterType();
	}, []);

	if (meterTypes?.length === 0 || tags?.length === 0) {
		return (
			<Spin>
				<div />
			</Spin>
		);
	}
	return (
		<Modal
			visible={true}
			title="Выгрузка показаний ОДПУ"
			okButtonProps={{ disabled: !date }}
			onOk={getMeterReport}
			onCancel={() => {
				if (!loading) {
					closeModal();
				}
			}}
		>
			<Spin spinning={loading}>
				<div style={{ display: "flex", flexDirection: "column" }}>
					<Form.Item>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<Tooltip
								title={
									selectedTag &&
									"Можно выгрузить только по типам счетчика или котельные"
								}
							>
								<Button.Group>
									{meterTypes?.map((meterType) => {
										return (
											<Button
												disabled={!!selectedTag}
												key={meterType.id}
												onClick={() => {
													if (meterType.id !== selectedMeterType) {
														setSelectedMeterType(meterType.id);
													} else {
														setSelectedMeterType();
													}
												}}
												className={classNames({
													[styles.activeButton]:
														meterType.id === selectedMeterType,
												})}
											>
												{meterType.name}
											</Button>
										);
									})}
								</Button.Group>
							</Tooltip>
							<TagsField
								tooltipTitle={
									selectedMeterType &&
									"Можно выгрузить только по типам счетчика или котельные"
								}
								disabled={!!selectedMeterType}
								setSelectedTag={selectTag}
								selectedTag={selectedTag}
							/>
						</div>
					</Form.Item>

					<Form.Item
						label="Выберите район"
						help={
							(selectedMeterType === 2 || selectedTag) &&
							"Для данного типа отчета не предусмотрен выбор районов"
						}
						colon={false}
					>
						<Checkbox
							disabled={selectedMeterType === 2 || !!selectedTag}
							checked={allRegions || selectedMeterType === 2 || !!selectedTag}
							onChange={selectAllRegions}
						>
							Все районы
						</Checkbox>
						<Select
							disabled={selectedMeterType === 2 || allRegions || !!selectedTag}
							placeholder={
								selectedMeterType === 2 || allRegions || selectedTag
									? "Все районы"
									: "Выберите районы, по которым нужен отчет"
							}
							mode="tags"
							style={{ width: "100%" }}
							tokenSeparators={[","]}
							value={selectedRegions}
							onChange={selectRegion}
						>
							{regions?.map((region) => {
								return (
									<Option key={region.id} value={`${region.id}`}>
										{region.name}
									</Option>
								);
							})}
						</Select>
					</Form.Item>
					<Form.Item label="Выберите месяц" colon={false}>
						<MonthPicker
							disabledDate={disableDate}
							onChange={selectMonth}
							placeholder="Выберите месяц"
							value={date}
						/>
					</Form.Item>
				</div>
			</Spin>
		</Modal>
	);
});

export default DownloadReport;
